import { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { createWorkflow, updateWorkflow } from '../services/workflow.service';
import type { WorkflowDefinition, WorkflowToolCatalog } from './workflow.types';
import { buildWorkflowPackage, parseWorkflowPackage } from './workflowPackage';
import HelpHint from '../components/HelpHint';

interface Props {
  workflow?: WorkflowDefinition | null;
  catalog: WorkflowToolCatalog | null;
  onImported: () => Promise<void>;
}

export default function WorkflowPackageControls({ workflow, catalog, onImported }: Props) {
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);

  const importPackage = async () => {
    if (!catalog || busy) return;
    setBusy(true);
    setError('');
    let createdId: string | undefined;
    try {
      const definition = parseWorkflowPackage(preview, catalog);
      const created = await createWorkflow({ name: `${definition.name} (imported)`, description: definition.description, trigger: definition.trigger });
      createdId = created?.data?._id;
      if (!createdId) throw new Error('The server did not return the created workflow.');
      const saved = await updateWorkflow(createdId, { nodes: definition.nodes, edges: definition.edges });
      if (!saved?.data) throw new Error('The server did not confirm the imported steps.');
      setPreview('');
      setNotice('Imported as an inactive workflow. Review skills, references and connections before activation.');
      await onImported();
    } catch (err) {
      setError(`${err instanceof Error ? err.message : 'Import failed.'}${createdId ? ' An inactive draft was created; check the workflow list before retrying.' : ''}`);
      if (createdId) await onImported();
    } finally { setBusy(false); }
  };

  return <Box sx={{ p: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
    <Button size="small" component="label" disabled={!catalog || busy}>
      Upload workflow
      <input hidden type="file" accept=".json,application/json" onChange={async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !catalog) return;
        setError(''); setNotice('');
        try {
          if (file.size > 2 * 1024 * 1024) throw new Error('Choose a workflow package smaller than 2 MB.');
          const text = await file.text();
          parseWorkflowPackage(text, catalog);
          setImporting(true); setPreview(text);
        } catch (err) { setError(err instanceof Error ? err.message : 'Unable to read the package.'); }
      }} />
    </Button>
    {workflow && <Button size="small" disabled={!catalog || busy} onClick={() => {
      if (!catalog) return;
      setError(''); setImporting(false);
      setPreview(JSON.stringify(buildWorkflowPackage(workflow, catalog), null, 2));
    }}>Export workflow</Button>}
    <HelpHint text="Tip: type /workflow in chat to build one with AI." />
    {error && <Alert severity="error">{error}</Alert>}
    {notice && <Alert severity="success">{notice}</Alert>}
    <Dialog open={!!preview} onClose={() => { if (!busy) setPreview(''); }} maxWidth="md" fullWidth>
      <DialogTitle>{importing ? 'Review workflow upload' : 'Workflow package — prompts, tools and settings'}</DialogTitle>
      <DialogContent>
        <Alert severity="info">{importing ? 'Creates an inactive copy. Tool definitions in the file are reference information; the destination tool catalog is used. Review skill IDs and connections after import.' : 'Includes step prompts, inputs, layout and tool descriptions. Skill contents and server model settings are not bundled. Named credential fields are redacted; review free-form text for sensitive information before sharing.'}</Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }}>{preview}</Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => setPreview('')}>Cancel</Button>
        <Button variant="contained" disabled={busy} onClick={importing ? importPackage : () => {
          const url = URL.createObjectURL(new Blob([preview], { type: 'application/json' }));
          const anchor = document.createElement('a');
          anchor.href = url; anchor.download = `${(workflow?.name || 'workflow').replace(/[^a-z0-9_-]/gi, '_')}.workflow.json`;
          anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
          setPreview('');
        }}>{busy ? 'Importing…' : importing ? 'Import inactive copy' : 'Download JSON'}</Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
